import { OrganisationType } from "src/types/dto/client.dto";
import { PermissionDTO } from 'src/types/dto/permission.dto';

export type OrToolsBusinessRule = {
  weekdays: number[];
  start_minute_of_day: number;
  end_minute_of_day: number;
};

export type OrToolsOptimizationWeights = {
  scheduled_task: number;
  priority: number;
  internal_inspector: number;
  preferred_inspector: number;
  travel_cost: number;
  hotel_night: number;
  team_size_penalty: number;
  same_parent_task_inspector_bonus: number;
  split_task_day_span_penalty: number;
  preferred_date_bonus: number;
  preferred_date_decay_per_day: number;
  preferred_date_early_penalty_per_day: number;
  preferred_date_range_bonus: number;
};

export type OrToolsSettings = {
  solve_mode: 'fast';
  business_rules: OrToolsBusinessRule[];
  optimization_weights: OrToolsOptimizationWeights;
};

export type UserType = {
  id?: string | number;
  displayName?: string;
  name?: string;
  email?: string;
  language?: string;
  dashboardLanguage?: string;
  communicationLanguages?: string[];
  preferredLanguage?: string;
  role?: string;
  roles?: string[];
  /** Present on session user from login/refresh when org may enforce MFA */
  hasMfaEnabled?: boolean;
  /** When false, dashboard APIs are blocked until email verification completes */
  isEmailVerified?: boolean;
  client?: {
    id?: number;
    name?: string;
    imageURL?: string;
    organisationType?: OrganisationType;
    timezoneId?: string;
    defaultLanguage?: string;
    templateLanguages?: string[];
    allowEditCustomers?: boolean;
    allowEditResources?: boolean;
    requireMfaForAllUsers?: boolean;
    enableAiAgent?: boolean;
    enableTravelBlockers?: boolean;
    ssoRequired?: boolean;
    useGoogleRoutesForTravelDistances?: boolean;
    enableFragmentedBookings?: boolean;
    enablePreRequestAutomation?: boolean;
    preRequestThreshold?: number;
    preRequestThresholdUnit?: 'day' | 'week' | 'month' | 'year';
    enableRequestAutomation?: boolean;
    requestThreshold?: number;
    requestThresholdUnit?: 'day' | 'week' | 'month' | 'year';
    useOrTools?: boolean;
    orToolsSettings?: OrToolsSettings;
  };
  rolesPermissions?: Array<PermissionDTO>;
  explicitPermissions?: Array<PermissionDTO>;
  inspector?: {
    id: number;
    name: string;
    email: string;
    type: string;
    clientId: number;
  };
  timezoneId?: string;
  businessUnits?: {
    id: number;
    name: string;
  }[]
} | null;

export type AuthState = {
  user: UserType;
  loading: boolean;
};

export type AuthContextValue = {
  user: UserType;
  loading: boolean;
  authenticated: boolean;
  unauthenticated: boolean;
  checkUserSession: () => Promise<void>;
  refreshUserToken: () => Promise<any>;
  signOut: () => Promise<void>;
};

export enum PermissionKeys {
  GET_INSPECTION_LINKS = 'GET_INSPECTION_LINKS',
  CREATE_INSPECTION_LINKS = 'CREATE_INSPECTION_LINKS',
  UPDATE_INSPECTION_LINKS = 'UPDATE_INSPECTION_LINKS',
  CREATE_TOKEN = 'CREATE_TOKEN',
  UPDATE_TOKEN = 'UPDATE_TOKEN',
  GET_CLIENT_USERS = 'GET_CLIENT_USERS',
  UPDATE_CLIENT_USERS = 'UPDATE_CLIENT_USERS',
  RESET_CLIENT_USER_MFA = 'RESET_CLIENT_USER_MFA',
  DELETE_CLIENT_USERS = 'DELETE_CLIENT_USERS',
  UPDATE_CLIENT_DETAILS = 'UPDATE_CLIENT_DETAILS',
  GET_API_KEYS = 'GET_API_KEYS',
  DELETE_API_KEYS = 'DELETE_API_KEYS',
  GENERATE_API_KEYS = 'GENERATE_API_KEYS',
  GET_CLIENT_HOOKS = 'GET_CLIENT_HOOKS',
  CREATE_CLIENT_HOOKS = 'CREATE_CLIENT_HOOKS',
  UPDATE_CLIENT_HOOKS = 'UPDATE_CLIENT_HOOKS',
  DELETE_CLIENT_HOOKS = 'DELETE_CLIENT_HOOKS',
  UPDATE_CLIENT_SUBSCRIPTION_PLAN_BASIC = 'UPDATE_CLIENT_SUBSCRIPTION_PLAN_BASIC',
  GET_USER_PROFILE = 'GET_USER_PROFILE',
  UPDATE_USER_PROFILE = 'UPDATE_USER_PROFILE',
  UPDATE_UNIT_CLIENT = 'UPDATE_UNIT_CLIENT',
  ACCESS_DASHBOARD_WITHOUT_SUBSCRIPTION = 'ACCESS_DASHBOARD_WITHOUT_SUBSCRIPTION',
  ACCESS_SETTINGS = 'ACCESS_SETTINGS',
  GET_CLIENTS = 'GET_CLIENTS',
  GET_CONFIGURATIONS = 'GET_CONFIGURATIONS',
  GET_DASHBOARD_STATISTICS = 'GET_DASHBOARD_STATISTICS',
  GET_CLIENTS_ACTIVITIES = 'GET_CLIENTS_ACTIVITIES',

  CREATE_SCHEME = 'CREATE_SCHEME',
  UPDATE_SCHEME = 'UPDATE_SCHEME',
  DELETE_SCHEME = 'DELETE_SCHEME',
  VIEW_SCHEMES = 'VIEW_SCHEMES',

  // Accreditation Bodies permissions
  VIEW_ACCREDITATION_BODIES = 'VIEW_ACCREDITATION_BODIES',
  CREATE_ACCREDITATION_BODY = 'CREATE_ACCREDITATION_BODY',
  UPDATE_ACCREDITATION_BODY = 'UPDATE_ACCREDITATION_BODY',
  DELETE_ACCREDITATION_BODY = 'DELETE_ACCREDITATION_BODY',
  MANAGE_ACCREDITATION_BODIES = 'MANAGE_ACCREDITATION_BODIES',

  VIEW_APPOINTMENTS = 'VIEW_APPOINTMENTS',
  CREATE_APPOINTMENTS = 'CREATE_APPOINTMENTS',
  UPDATE_APPOINTMENTS = 'UPDATE_APPOINTMENTS',
  DELETE_APPOINTMENTS = 'DELETE_APPOINTMENTS',

  VIEW_COMPETENCIES = 'VIEW_COMPETENCIES',
  CREATE_COMPETENCIES = 'CREATE_COMPETENCIES',
  UPDATE_COMPETENCIES = 'UPDATE_COMPETENCIES',
  DELETE_COMPETENCIES = 'DELETE_COMPETENCIES',

  CREATE_CUSTOMER = 'CREATE_CUSTOMER',
  UPDATE_CUSTOMER = 'UPDATE_CUSTOMER',
  DELETE_CUSTOMER = 'DELETE_CUSTOMER',
  VIEW_CUSTOMERS = 'VIEW_CUSTOMERS',

  CREATE_PROJECT = 'CREATE_PROJECT',
  UPDATE_PROJECT = 'UPDATE_PROJECT',
  DELETE_PROJECT = 'DELETE_PROJECT',
  VIEW_PROJECTS = 'VIEW_PROJECTS',
  CAN_OWN_PROJECT = 'CAN_OWN_PROJECT',

  VIEW_PROGRAMMES = 'VIEW_PROGRAMMES',
  CREATE_PROGRAMME = 'CREATE_PROGRAMME',
  UPDATE_PROGRAMME = 'UPDATE_PROGRAMME',
  SUBMIT_PROGRAMME = 'SUBMIT_PROGRAMME',
  REVIEW_PROGRAMME = 'REVIEW_PROGRAMME',
  COMPLETE_PROGRAMME = 'COMPLETE_PROGRAMME',

  CREATE_SERVICE_TYPE = 'CREATE_SERVICE_TYPE',
  UPDATE_SERVICE_TYPE = 'UPDATE_SERVICE_TYPE',
  DELETE_SERVICE_TYPE = 'DELETE_SERVICE_TYPE',
  VIEW_SERVICE_TYPES = 'VIEW_SERVICE_TYPES',
  UPDATE_SERVICE_STATUS = 'UPDATE_SERVICE_STATUS',

  CREATE_SERVICE = 'CREATE_SERVICE',
  UPDATE_SERVICE = 'UPDATE_SERVICE',
  DELETE_SERVICE = 'DELETE_SERVICE',
  VIEW_SERVICES = 'VIEW_SERVICES',

  CREATE_SITE = 'CREATE_SITE',
  UPDATE_SITE = 'UPDATE_SITE',
  DELETE_SITE = 'DELETE_SITE',
  VIEW_SITES = 'VIEW_SITES',

  CREATE_TEAM = 'CREATE_TEAM',
  UPDATE_TEAM = 'UPDATE_TEAM',
  DELETE_TEAM = 'DELETE_TEAM',
  VIEW_TEAMS = 'VIEW_TEAMS',

  CREATE_PREFERRENCE = 'CREATE_PREFERRENCE',
  UPDATE_PREFERENCE = 'UPDATE_PREFERENCE',
  DELETE_PREFERENCE = 'DELETE_PREFERENCE',
  VIEW_PREFERENCE = 'VIEW_PREFERENCE',

  VIEW_REGIONS = 'VIEW_REGIONS',
  CREATE_REGION = 'CREATE_REGION',
  UPDATE_REGION = 'UPDATE_REGION',
  DELETE_REGION = 'DELETE_REGION',

  VIEW_MEMBER_ROLES = 'VIEW_MEMBER_ROLES',
  CREATE_MEMBER_ROLE = 'CREATE_MEMBER_ROLE',
  UPDATE_MEMBER_ROLE = 'UPDATE_MEMBER_ROLE',
  DELETE_MEMBER_ROLE = 'DELETE_MEMBER_ROLE',

  VIEW_CLIENT_TYPES = 'VIEW_CLIENT_TYPES',
  CREATE_CLIENT_TYPE = 'CREATE_CLIENT_TYPE',
  UPDATE_CLIENT_TYPE = 'UPDATE_CLIENT_TYPE',
  DELETE_CLIENT_TYPE = 'DELETE_CLIENT_TYPE',

  VIEW_ACTIVITIES = 'VIEW_ACTIVITIES',
  CREATE_ACTIVITY = 'CREATE_ACTIVITY',
  UPDATE_ACTIVITY = 'UPDATE_ACTIVITY',
  DELETE_ACTIVITY = 'DELETE_ACTIVITY',
  VIEW_ACTIVITY_AUDIT_TRAIL = 'VIEW_ACTIVITY_AUDIT_TRAIL',
  VIEW_ACTIVITY_PREFERRED_DATES = 'VIEW_ACTIVITY_PREFERRED_DATES',
  CREATE_ACTIVITY_PREFERRED_DATE = 'CREATE_ACTIVITY_PREFERRED_DATE',
  UPDATE_ACTIVITY_PREFERRED_DATE = 'UPDATE_ACTIVITY_PREFERRED_DATE',
  DELETE_ACTIVITY_PREFERRED_DATE = 'DELETE_ACTIVITY_PREFERRED_DATE',
  BATCH_CREATE_ACTIVITY_EXCLUDED_DATES = 'BATCH_CREATE_ACTIVITY_EXCLUDED_DATES',

  // New client permissions
  VIEW_CLIENTS = 'VIEW_CLIENTS',
  CREATE_CLIENT = 'CREATE_CLIENT',
  UPDATE_CLIENT = 'UPDATE_CLIENT',
  DELETE_CLIENT = 'DELETE_CLIENT',

  // New inspector permissions
  VIEW_INSPECTORS = 'VIEW_INSPECTORS',
  CREATE_INSPECTOR = 'CREATE_INSPECTOR',
  UPDATE_INSPECTOR = 'UPDATE_INSPECTOR',
  DELETE_INSPECTOR = 'DELETE_INSPECTOR',

  // Organization management permissions (admin-only)
  VIEW_ORGANIZATION = 'VIEW_ORGANIZATION',
  MANAGE_ORGANIZATION = 'MANAGE_ORGANIZATION',
  CREATE_ORGANIZATION_USERS = 'CREATE_ORGANIZATION_USERS',
  UPDATE_ORGANIZATION_USERS = 'UPDATE_ORGANIZATION_USERS',
  DELETE_ORGANIZATION_USERS = 'DELETE_ORGANIZATION_USERS',
  SEND_INVITATION_EMAILS = 'SEND_INVITATION_EMAILS',

  VIEW_NATIONAL_HOLIDAY = 'VIEW_NATIONAL_HOLIDAY',
  CREATE_NATIONAL_HOLIDAY = 'CREATE_NATIONAL_HOLIDAY',
  UPDATE_NATIONAL_HOLIDAY = 'UPDATE_NATIONAL_HOLIDAY',
  DELETE_NATIONAL_HOLIDAY = 'DELETE_NATIONAL_HOLIDAY',
  VIEW_WORKING_DAYS = 'VIEW_WORKING_DAYS',
  CREATE_WORKING_DAYS = 'CREATE_WORKING_DAYS',
  UPDATE_WORKING_DAYS = 'UPDATE_WORKING_DAYS',
  DELETE_WORKING_DAYS = 'DELETE_WORKING_DAYS',

  SPLIT_AND_COMBINE_APPOINTMENTS = 'SPLIT_AND_COMBINE_APPOINTMENTS',
  DASHBOARD_PAGE_VIEW = 'DASHBOARD_PAGE_VIEW',

  APPROVE_SOLUTION_APPOINTMENTS = 'APPROVE_SOLUTION_APPOINTMENTS',
  SUSPEND_CLIENT_USERS = 'SUSPEND_CLIENT_USERS',
  ACTIVATE_CLIENT_USERS = 'ACTIVATE_CLIENT_USERS',
  INVITE_CLIENT_USERS = 'INVITE_CLIENT_USERS',
  CREATE_CLIENT_USERS = 'CREATE_CLIENT_USERS',
  VIEW_AUDIT_TRAILS = 'VIEW_AUDIT_TRAILS',

  // Tasks permissions
  CREATE_TASKS = 'CREATE_TASKS',
  UPDATE_TASKS = 'UPDATE_TASKS',
  DELETE_TASKS = 'DELETE_TASKS',
  VIEW_TASKS = 'VIEW_TASKS',
  GET_USERS = 'GET_USERS',

  // Business Units permissions
  VIEW_BUSINESS_UNITS = 'VIEW_BUSINESS_UNITS',
  CREATE_BUSINESS_UNIT = 'CREATE_BUSINESS_UNIT',
  UPDATE_BUSINESS_UNIT = 'UPDATE_BUSINESS_UNIT',
  DELETE_BUSINESS_UNIT = 'DELETE_BUSINESS_UNIT',
  ASSIGN_BUSINESS_UNIT = 'ASSIGN_BUSINESS_UNIT',

  VIEW_LEAVES = 'VIEW_LEAVES',
  CREATE_LEAVE = 'CREATE_LEAVE',
  UPDATE_LEAVE = 'UPDATE_LEAVE',
  DELETE_LEAVE = 'DELETE_LEAVE',

  VIEW_FILE_IMPORT = 'VIEW_FILE_IMPORT',
  CREATE_FILE_IMPORT = 'CREATE_FILE_IMPORT',
  UPDATE_FILE_IMPORT = 'UPDATE_FILE_IMPORT',
  DELETE_FILE_IMPORT = 'DELETE_FILE_IMPORT',

  CREATE_PROJECTS_ATTACHMENTS = 'CREATE_PROJECTS_ATTACHMENTS',
  VIEW_PROJECTS_ATTACHMENTS = 'VIEW_PROJECTS_ATTACHMENTS',
  UPDATE_PROJECTS_ATTACHMENTS = 'UPDATE_PROJECTS_ATTACHMENTS',
  DELETE_PROJECTS_ATTACHMENTS = 'DELETE_PROJECTS_ATTACHMENTS',
  DOWNLOAD_PROJECTS_ATTACHMENTS = 'DOWNLOAD_PROJECTS_ATTACHMENTS',

  VIEW_REPORTS = 'VIEW_REPORTS',
  EXPORT_REPORTS = 'EXPORT_REPORTS',
  VIEW_SOLUTIONS = 'VIEW_SOLUTIONS',

  // Notification permissions
  VIEW_NOTIFICATIONS = 'VIEW_NOTIFICATIONS',
  UPDATE_NOTIFICATIONS = 'UPDATE_NOTIFICATIONS',

  VIEW_MEMBER_PROFILE_PAGE = 'VIEW_MEMBER_PROFILE_PAGE',

  // Contact people (selectable on organisational roles: View + Manage)
  VIEW_CONTACT_PEOPLE = 'VIEW_CONTACT_PEOPLE',
  CREATE_CONTACT_PERSON = 'CREATE_CONTACT_PERSON',
  UPDATE_CONTACT_PERSON = 'UPDATE_CONTACT_PERSON',
  DELETE_CONTACT_PERSON = 'DELETE_CONTACT_PERSON',

  // Internal permissions (cascade-only, not selectable in org roles modal)
  VIEW_PROJECT_NOTES = 'VIEW_PROJECT_NOTES',
  VIEW_PROJECT_ROLES = 'VIEW_PROJECT_ROLES',

  // Email Template permissions
  VIEW_EMAIL_TEMPLATES = 'VIEW_EMAIL_TEMPLATES',
  CREATE_EMAIL_TEMPLATE = 'CREATE_EMAIL_TEMPLATE',
  UPDATE_EMAIL_TEMPLATE = 'UPDATE_EMAIL_TEMPLATE',
  DELETE_EMAIL_TEMPLATE = 'DELETE_EMAIL_TEMPLATE',

  // Organizational roles feature
  VIEW_ORGANIZATIONAL_ROLES = 'VIEW_ORGANIZATIONAL_ROLES',
  CREATE_ORGANIZATIONAL_ROLE = 'CREATE_ORGANIZATIONAL_ROLE',
  UPDATE_ORGANIZATIONAL_ROLE = 'UPDATE_ORGANIZATIONAL_ROLE',
  DELETE_ORGANIZATIONAL_ROLE = 'DELETE_ORGANIZATIONAL_ROLE',
  CREATE_CUSTOM_ROLE = 'CREATE_CUSTOM_ROLE',
  UPDATE_CUSTOM_ROLE = 'UPDATE_CUSTOM_ROLE',
  DELETE_CUSTOM_ROLE = 'DELETE_CUSTOM_ROLE',
  ASSIGN_CUSTOM_ROLES = 'ASSIGN_CUSTOM_ROLES',
  INVALID_PERMISSION = 'INVALID_PERMISSION', // use to prevent access to a route

  VIEW_WEBHOOKS = 'VIEW_WEBHOOKS',
  CREATE_WEBHOOKS = 'CREATE_WEBHOOKS',
  UPDATE_WEBHOOKS = 'UPDATE_WEBHOOKS',
  DELETE_WEBHOOKS = 'DELETE_WEBHOOKS',
  VIEW_WEBHOOK_LOGS = 'VIEW_WEBHOOK_LOGS',
  UPDATE_WEBHOOK_LOGS = 'UPDATE_WEBHOOK_LOGS',
  CREATE_PROJECT_NOTE = 'CREATE_PROJECT_NOTE',
  UPDATE_PROJECT_NOTE = 'UPDATE_PROJECT_NOTE',
  DELETE_PROJECT_NOTE = 'DELETE_PROJECT_NOTE',

  CONNECT_CALENDAR = 'CONNECT_CALENDAR',

  // Email Outbox permissions
  VIEW_EMAIL_OUTBOX = 'VIEW_EMAIL_OUTBOX',
  APPROVE_EMAIL_OUTBOX = 'APPROVE_EMAIL_OUTBOX',
}

export type PermissionLevel = 'OWN' | 'GROUP' | 'ALL' | 'MY_TASKS' | 'MY_PROJECTS' | 'ACTIVITIES_UNDER_MY_PROJECTS';
